"use client"

import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import axios from "axios"
import { useNavigate } from "react-router-dom"
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CircleDollarSign,
  Download,
  FileText,
  RefreshCw,
  Ticket,
} from "lucide-react"
import "../styles/meusBoletos.css"
import { UserContext } from "../context/UserContext"

const API = "https://api.ticketdovaqueiro.com/api"

const STATUS_PARCELA = {
  paga: {
    label: "PAGO",
    className: "mb-status-paga",
    icon: <CheckCircle2 size={15} />,
  },
  pendente: {
    label: "EM ABERTO",
    className: "mb-status-pendente",
    icon: <CircleDollarSign size={15} />,
  },
  vencida: {
    label: "ATRASADO",
    className: "mb-status-vencida",
    icon: <AlertTriangle size={15} />,
  },
  cancelada: {
    label: "CANCELADO",
    className: "mb-status-cancelada",
    icon: <FileText size={15} />,
  },
}

const formatarDinheiro = (valor) =>
  Number(valor || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  })

const formatarData = (data) => {
  if (!data) return "—"
  return new Date(data).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "America/Sao_Paulo",
  })
}

const MeusBoletos = () => {
  const navigate = useNavigate()
  const { user, token, isLoading } = useContext(UserContext)

  const [dados, setDados] = useState({ resumo: null, parcelamentos: [] })
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState("")
  const [abertoId, setAbertoId] = useState(null)

  const carregarBoletos = useCallback(async () => {
    if (!user?._id || !token) {
      navigate("/login?redirect=/meus-boletos")
      return
    }

    try {
      setLoading(true)
      setErro("")

      const { data } = await axios.get(`${API}/pagamento/parcelados/meus`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      setDados({
        resumo: data?.resumo || null,
        parcelamentos: Array.isArray(data?.parcelamentos) ? data.parcelamentos : [],
      })
    } catch (err) {
      console.error("Erro ao carregar boletos parcelados:", err)
      const status = err?.response?.status

      if (status === 401 || status === 403) {
        navigate("/login?redirect=/meus-boletos")
        return
      }

      setErro("Não foi possível carregar seus boletos. Toque em tentar novamente.")
    } finally {
      setLoading(false)
    }
  }, [navigate, token, user?._id])

  useEffect(() => {
    if (!isLoading) carregarBoletos()
  }, [isLoading, carregarBoletos])

  const primeiroNome = useMemo(
    () => user?.nomeCompleto?.trim()?.split(" ")?.[0] || "cliente",
    [user?.nomeCompleto]
  )

  const resumo = dados.resumo || {
    emAberto: { quantidade: 0, valor: 0 },
    atrasadas: { quantidade: 0, valor: 0 },
    pagas: { quantidade: 0, valor: 0 },
  }

  return (
    <main className="mb-page">
      <section className="mb-container">
        <header className="mb-header">
          <div className="mb-header-icon">
            <FileText size={27} />
          </div>

          <div>
            <p className="mb-eyebrow">ÁREA DO CLIENTE</p>
            <h1>Meus Boletos</h1>
            <p className="mb-subtitle">
              Olá, {primeiroNome}. Veja e pague suas parcelas por aqui.
            </p>
          </div>
        </header>

        {loading ? (
          <section className="mb-state-card">
            <RefreshCw size={34} className="mb-spinner" />
            <strong>Carregando seus boletos...</strong>
          </section>
        ) : erro ? (
          <section className="mb-state-card mb-state-error">
            <AlertTriangle size={36} />
            <strong>{erro}</strong>
            <button className="mb-retry-button" onClick={carregarBoletos}>
              TENTAR NOVAMENTE
            </button>
          </section>
        ) : dados.parcelamentos.length === 0 ? (
          <section className="mb-state-card">
            <Ticket size={38} />
            <strong>Você não tem boletos parcelados.</strong>
            <span>Quando houver uma compra parcelada, ela aparecerá aqui.</span>
          </section>
        ) : (
          <>
            <section className="mb-summary-grid" aria-label="Resumo dos boletos">
              <article className="mb-summary-card mb-summary-open">
                <CircleDollarSign size={21} />
                <div>
                  <span>EM ABERTO</span>
                  <strong>{resumo.emAberto.quantidade}</strong>
                  <small>{formatarDinheiro(resumo.emAberto.valor)}</small>
                </div>
              </article>

              <article className="mb-summary-card mb-summary-late">
                <AlertTriangle size={21} />
                <div>
                  <span>EM ATRASO</span>
                  <strong>{resumo.atrasadas.quantidade}</strong>
                  <small>{formatarDinheiro(resumo.atrasadas.valor)}</small>
                </div>
              </article>

              <article className="mb-summary-card mb-summary-paid">
                <CheckCircle2 size={21} />
                <div>
                  <span>PAGOS</span>
                  <strong>{resumo.pagas.quantidade}</strong>
                  <small>{formatarDinheiro(resumo.pagas.valor)}</small>
                </div>
              </article>
            </section>

            <p className="mb-simple-hint">
              Toque em um pedido para ver as parcelas.
            </p>

            <section className="mb-orders-list">
              {dados.parcelamentos.map((parcelamento) => {
                const estaAberto = abertoId === parcelamento.id
                const possuiAtraso = parcelamento.parcelas.some(
                  (parcela) => parcela.status === "vencida"
                )

                return (
                  <article
                    className={`mb-order-card ${possuiAtraso ? "mb-order-late" : ""}`}
                    key={parcelamento.id}
                  >
                    <button
                      className="mb-order-toggle"
                      type="button"
                      onClick={() => setAbertoId(estaAberto ? null : parcelamento.id)}
                      aria-expanded={estaAberto}
                    >
                      <span className="mb-event-icon">
                        <Ticket size={19} />
                      </span>

                      <span className="mb-order-main">
                        <strong>{parcelamento.evento.nome}</strong>
                        <small>
                          {parcelamento.parcelasPagas} de {parcelamento.numeroParcelas} parcelas pagas
                        </small>
                      </span>

                      <span className={possuiAtraso ? "mb-order-alert" : "mb-order-value"}>
                        {possuiAtraso ? "VER ATRASO" : formatarDinheiro(parcelamento.valorParcela)}
                      </span>

                      <span className="mb-chevron">
                        {estaAberto ? <ChevronUp size={21} /> : <ChevronDown size={21} />}
                      </span>
                    </button>

                    {estaAberto && (
                      <div className="mb-installments">
                        <div className="mb-order-info">
                          <span>{parcelamento.pedido.nomeVaqueiro}</span>
                          {parcelamento.pedido.categoria && (
                            <span>{parcelamento.pedido.categoria}</span>
                          )}
                        </div>

                        {parcelamento.parcelas.map((parcela) => {
                          const status = STATUS_PARCELA[parcela.status] || STATUS_PARCELA.pendente
                          const podeAbrirBoleto =
                            Boolean(parcela.boletoUrl) &&
                            (parcela.status === "pendente" || parcela.status === "vencida")

                          return (
                            <div className="mb-installment-row" key={`${parcelamento.id}-${parcela.numero}`}>
                              <div className="mb-installment-title">
                                <strong>PARCELA {parcela.numero}</strong>
                                <span>
                                  <CalendarDays size={14} />
                                  Vence em {formatarData(parcela.vencimento)}
                                </span>
                              </div>

                              <div className="mb-installment-value">
                                <strong>{formatarDinheiro(parcela.valor)}</strong>
                                <span className={`mb-status ${status.className}`}>
                                  {status.icon}
                                  {status.label}
                                </span>
                              </div>

                              {podeAbrirBoleto ? (
                                <a
                                  className="mb-download-button"
                                  href={parcela.boletoUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <Download size={17} />
                                  ABRIR BOLETO
                                </a>
                              ) : (
                                <span className="mb-paid-note">
                                  {parcela.status === "paga"
                                    ? `Pago em ${formatarData(parcela.dataPagamento)}`
                                    : "Boleto indisponível"}
                                </span>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </article>
                )
              })}
            </section>
          </>
        )}
      </section>
    </main>
  )
}

export default MeusBoletos
